import { useMemo, useState } from "react";
import { WeekNavigator } from "../components/WeekNavigator";
import { useCoachSchedule } from "../hooks/useCoachSchedule";
import { ApiError, gymClient } from "../apiClient/gymClient";
import { addDays, formatFullDate, parseBookingSlotId, startOfToday, toISODate } from "../utils/dates";
import type { BookingWithAttendee } from "../types/gym.types";

function AttendeeRow({ booking, onUpdated }: { booking: BookingWithAttendee; onUpdated: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmingNoShow, setConfirmingNoShow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckIn() {
    setSubmitting(true);
    setError(null);
    try {
      await gymClient.checkInBooking(booking.id);
      onUpdated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't check this booking in.");
      setSubmitting(false);
    }
  }

  async function handleNoShow() {
    setSubmitting(true);
    setError(null);
    try {
      await gymClient.markNoShow(booking.id);
      onUpdated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't mark this as a no-show.");
      setSubmitting(false);
      setConfirmingNoShow(false);
    }
  }

  return (
    <li className="booking-row">
      <span className="attendee">
        <span className="attendee-name">{booking.fullName}</span>
        <span className="attendee-mobile">{booking.mobileNumber}</span>
      </span>

      {booking.status === "confirmed" ? (
        confirmingNoShow ? (
          <div className="cancel-confirm">
            <span>Mark as no-show?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmingNoShow(false)} disabled={submitting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleNoShow} disabled={submitting}>
              {submitting ? "Marking…" : "Yes, no-show"}
            </button>
          </div>
        ) : (
          <div className="coach-actions">
            <button type="button" className="btn btn-primary" onClick={handleCheckIn} disabled={submitting}>
              {submitting ? "Marking…" : "Attended"}
            </button>
            <button
              type="button"
              className="btn btn-danger-ghost"
              onClick={() => setConfirmingNoShow(true)}
              disabled={submitting}
            >
              No show
            </button>
          </div>
        )
      ) : booking.status === "attended" ? (
        <span className="status-badge status-badge--attended">Attended</span>
      ) : (
        <span className="status-badge status-badge--no-show">No-show</span>
      )}
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

function SlotGroup({
  startTime,
  bookings,
  onUpdated,
}: {
  startTime: string;
  bookings: BookingWithAttendee[];
  onUpdated: () => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const pending = bookings.filter((b) => b.status === "confirmed").length;

  return (
    <div className="coach-slot-group">
      <button type="button" className="coach-slot-header" onClick={() => setIsOpen((o) => !o)}>
        <span className="coach-slot-time">{startTime}</span>
        <span className="coach-slot-count">
          {bookings.length} booked{pending > 0 ? ` · ${pending} pending` : ""}
        </span>
        <span className="coach-slot-chevron" aria-hidden="true">
          {isOpen ? "−" : "+"}
        </span>
      </button>
      {isOpen && (
        <ul className="bookings-day-items">
          {bookings.map((b) => (
            <AttendeeRow key={b.id} booking={b} onUpdated={onUpdated} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function CoachCheckInPage() {
  const [date, setDate] = useState(() => toISODate(startOfToday()));
  const [query, setQuery] = useState("");
  const { bookings, isLoading, error, refresh } = useCoachSchedule(date);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) => b.fullName.toLowerCase().includes(q));
  }, [bookings, query]);

  const bySlot = useMemo(() => {
    const map = new Map<string, BookingWithAttendee[]>();
    for (const booking of filtered) {
      const { startTime } = parseBookingSlotId(booking.slotId);
      const list = map.get(startTime) ?? [];
      list.push(booking);
      map.set(startTime, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Coach Check-in</h1>
          <p className="page-sub">Pick a slot to mark who attended or didn&rsquo;t show.</p>
        </div>
      </header>

      <div className="schedule-toolbar">
        <WeekNavigator
          label={formatFullDate(date)}
          onPrev={() => setDate((d) => toISODate(addDays(new Date(`${d}T00:00:00`), -1)))}
          onNext={() => setDate((d) => toISODate(addDays(new Date(`${d}T00:00:00`), 1)))}
          canPrev
          canNext
        />
        <input
          type="text"
          className="coach-search"
          placeholder="Search by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search by name"
        />
      </div>

      {error ? (
        <div className="state-panel">
          <h2>Couldn&rsquo;t load the roster</h2>
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : bySlot.length === 0 ? (
        <div className="state-panel">
          <p>{bookings.length === 0 ? "No bookings for this day." : "No one matches that search."}</p>
        </div>
      ) : (
        <div className="coach-slots">
          {bySlot.map(([startTime, slotBookings]) => (
            <SlotGroup key={startTime} startTime={startTime} bookings={slotBookings} onUpdated={() => void refresh()} />
          ))}
        </div>
      )}
    </div>
  );
}
